// src/components/dfs/CurrentLineup.tsx

import { type ReactNode, type RefObject } from 'react';
import { formatPlayerDisplayName } from '@/utils/formatting';
import { formatMatchupAbbreviation } from '@/utils/teams';
import { formatMatchTime, formatMatchDateTime, getOprkColorClass, OPRK_TOOLTIP } from '@/utils/playerDisplay';
import { useNow } from '@/hooks/useNow';
import AvailabilityBadge from '@/components/common/AvailabilityBadge';
import InjuryBadge from '@/components/common/InjuryBadge';
import { AnimatedScore } from '@/components/common/AnimatedNumericDisplay';
import { useScoreChangeHighlight } from '@/hooks/useScoreChangeHighlight';

// Define SlotType
type SlotType = 'FW' | 'MF' | 'DF' | 'FLEX';

// Interface for player data included in the lineup state
export interface LineupPlayer {
  id: number;
  name?: string | null;
  nickname?: string | null;
  current_team_id?: number | null;
  current_team_name?: string | null;
  primary_position?: string | null;
  dfs_salary?: number | null;
  fppg?: number | null;
  oprk?: number | null;
  opponent?: string | null;
  match_time?: string | null;
  availability_status?: 'STARTING' | 'BENCH' | 'NOT_IN_SQUAD' | 'LINEUPS_TBD' | null;
  injury_status?: string | null;
  injury_details?: string | null;
  assigned_slot: string;
  // Live match score fields
  home_score?: number | null;
  away_score?: number | null;
  match_clock?: string | null;
  home_team_abbr?: string | null;
  away_team_abbr?: string | null;
  // Contest mode fields
  current_score?: number | null;
}

interface CurrentLineupProps {
  lineup: LineupPlayer[];
  onRemovePlayer: (playerId: number) => void;
  onAddPlayerClick: (slotType: SlotType) => void;
  onPlayerClick: (player: LineupPlayer) => void;
  remainingSalary: number;
  lockedPlayerIds: Set<number>;
  leagueShortNameForOprkContext?: string | null;
  activeSlot?: SlotType | null; // desktop: which slot is currently being filled
  firstEmptySlotRef?: RefObject<HTMLDivElement | null>; // builder: anchor for the first-empty-slot coach mark
  // Contest mode props
  mode?: 'builder' | 'contest';
  onNavigateToBuilder?: () => void;
  previousPlayerScores?: Record<string, number>;
  onScoreClick?: (player: LineupPlayer) => void;
  onMatchInfoClick?: (player: LineupPlayer) => void;
}

const SLOTS_CONFIG: { key: SlotType; label: string; count: number }[] = [
    { key: 'FW', label: 'Forward', count: 2 },
    { key: 'MF', label: 'Midfielder', count: 2 },
    { key: 'DF', label: 'Defender', count: 2 },
    { key: 'FLEX', label: 'Flex', count: 2 },
];

/** Wrapper that applies a green background flash when a player's score changes. */
function ScoreHighlightWrapper({ score, children, className }: { score: number; children: ReactNode; className?: string }) {
    const { isHighlight } = useScoreChangeHighlight(score);
    return (
        <div className={`${className ?? ''} transition-colors duration-[1500ms] ${isHighlight ? 'bg-green-500/15' : ''}`}>
            {children}
        </div>
    );
}

function CurrentLineup({
    lineup,
    onRemovePlayer,
    onAddPlayerClick,
    onPlayerClick,
    remainingSalary,
    lockedPlayerIds,
    activeSlot,
    firstEmptySlotRef,
    mode = 'builder',
    onNavigateToBuilder,
    previousPlayerScores,
    onScoreClick,
    onMatchInfoClick,
}: CurrentLineupProps) {
  const now = useNow();
  const isContest = mode === 'contest';

  const getPlayersInSlot = (slotKey: string): LineupPlayer[] => {
    return lineup.filter(p => p.assigned_slot === slotKey);
  };

  // First empty slot in render order (FW, MF, DF, FLEX). Anchors the one-time
  // builder coach mark; null once every slot is filled.
  const firstEmptySlot = (() => {
    for (const { key, count } of SLOTS_CONFIG) {
      const filled = getPlayersInSlot(key).length;
      if (filled < count) return { slotKey: key as SlotType, index: filled };
    }
    return null;
  })();

  return (
    <div className="bg-surface-card p-2 sm:p-4 lg:p-2.5 rounded-lg shadow-lg text-gray-200 font-body border-2 border-accent-yellow/30 hover:border-accent-yellow/60 transition-colors duration-200">
      {!isContest && (
        <div className="flex justify-center items-baseline mb-2 border-b border-gray-700/50 pb-2 text-center lg:hidden">
          <span className="font-heading font-bold text-base sm:text-lg text-accent-yellow uppercase tracking-wider mr-2">
              BUDGET REMAINING:
          </span>
          <span className={`font-heading font-bold text-base sm:text-lg ${ remainingSalary < 0 ? 'text-secondary' : 'text-accent-yellow' }`}>
              ${remainingSalary.toLocaleString()}
          </span>
        </div>
      )}

      <div className="space-y-1 lg:space-y-1.5">
        {SLOTS_CONFIG.map(({ key: slotKey, label: slotLabel, count: slotLimit }) => {
           const playersInThisSlot = getPlayersInSlot(slotKey);
           const slotsToRender = Array.from({ length: slotLimit });

           return (
                <div key={slotKey}>
                    <div className="space-y-1 lg:space-y-1">
                        {slotsToRender.map((_, index) => {
                            const player = playersInThisSlot[index];
                            const isLocked = player ? lockedPlayerIds.has(player.id) : false;

                            const handlePlayerRowClick = () => {
                                if (isContest && isLocked && player) {
                                    // Locked contest player — open profile
                                    onPlayerClick(player);
                                } else if (isContest && !isLocked && player) {
                                    // Unlocked contest player — open profile
                                    onPlayerClick(player);
                                } else if (isContest && !player) {
                                    // Empty slot in contest mode — go to builder
                                    onNavigateToBuilder?.();
                                } else if (player && !isLocked && onPlayerClick) {
                                    // Builder mode: player not locked — trigger edit
                                    onPlayerClick(player);
                                } else if (!player && onAddPlayerClick) {
                                    // Builder mode: empty slot — trigger add player
                                    onAddPlayerClick(slotKey);
                                }
                            };

                            const isRowInteractive = isContest || (player && !isLocked && onPlayerClick) || (!player && onAddPlayerClick);
                            const lockedStyle = (!isContest && isLocked) ? 'opacity-60 cursor-not-allowed' : '';
                            // ESPN drives the matchup display. Presence of abbrs = we have ESPN data for this match.
                            // match_clock discriminates state: null = pre-match, FT/AET/PEN = final, anything else = live.
                            const hasEspnMatchData = !!(player?.home_team_abbr && player?.away_team_abbr);
                            const isPreMatch = hasEspnMatchData && player?.match_clock == null;
                            const isFinalMatch = hasEspnMatchData && !!player?.match_clock && ['FT','AET','PEN'].includes(player.match_clock);
                            const matchupDisplay = player
                                ? (hasEspnMatchData
                                    ? (isPreMatch
                                        ? `${player.home_team_abbr} v ${player.away_team_abbr}`
                                        : `${player.home_team_abbr} ${player.home_score ?? 0}-${player.away_score ?? 0} ${player.away_team_abbr}`)
                                    : formatMatchupAbbreviation(player.current_team_name, player.opponent))
                                : '';

                            const score = player?.current_score ?? 0;
                            const previousScore = player ? previousPlayerScores?.[player.id] : undefined;

                            const rowKey = `${slotKey}-${index}`;
                            const rowContent = (
                                <div
                                    key={rowKey}
                                    ref={firstEmptySlotRef && firstEmptySlot?.slotKey === slotKey && firstEmptySlot?.index === index ? firstEmptySlotRef : undefined}
                                    className={`flex items-center px-3 py-1.5 lg:py-1.5 lg:px-2 min-h-[48px] lg:min-h-[44px] rounded text-sm leading-tight transition-colors duration-150 ease-in-out shadow-sm
                                        ${ player
                                            ? isContest && isLocked
                                                ? 'bg-primary/70 border border-accent-yellow/20 group/slot hover:bg-primary/90 active:bg-primary/90 cursor-pointer'
                                                : `bg-primary/70 border border-accent-yellow/20 ${lockedStyle} ${!isLocked ? 'group/slot hover:bg-primary/90 active:bg-primary/90 cursor-pointer' : ''}`
                                            : activeSlot === slotKey && index === playersInThisSlot.length
                                                ? 'bg-accent-yellow/10 border border-accent-yellow/60 hover:bg-accent-yellow/15 active:bg-accent-yellow/15 cursor-pointer'
                                                : 'bg-primary/30 border border-accent-yellow/10 hover:bg-primary/50 active:bg-primary/50 cursor-pointer'
                                        }`}
                                    onClick={handlePlayerRowClick}
                                    role={isRowInteractive ? 'button' : undefined}
                                    tabIndex={isRowInteractive ? 0 : undefined}
                                    aria-label={!player ? `Add ${slotLabel}` : (isContest ? `View ${formatPlayerDisplayName(player)}` : `Edit ${formatPlayerDisplayName(player)}`)}
                                >
                                    {player ? (
                                        <>
                                            <div className="flex-grow flex items-center space-x-2 sm:space-x-3 overflow-hidden mr-2">
                                                <span className={`font-bold text-xs w-7 text-center flex-shrink-0 ${(!isContest && isLocked) ? 'text-gray-500' : 'text-accent-yellow'}`}>
                                                    {player.assigned_slot}
                                                </span>
                                                <div className="flex justify-between items-center min-w-0 flex-grow">

                                                    {/* LEFT COLUMN: Name and Stats. Match availability
                                                        lives with the fixture, preserving name width. */}
                                                    <div className="flex flex-col min-w-0 mr-2">
                                                        <div className="min-w-0">
                                                            <span className={`break-words font-medium text-gray-100 text-sm leading-tight lg:text-xs ${(!isContest && isLocked) ? '' : 'group-hover/slot:text-accent-yellow group-hover/slot:underline group-hover/slot:underline-offset-2 group-active/slot:text-accent-yellow group-active/slot:underline group-active/slot:underline-offset-2'}`} title={formatPlayerDisplayName(player)}>
                                                                {formatPlayerDisplayName(player)}
                                                            </span>
                                                        </div>

                                                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-badge leading-tight sm:text-xs lg:mt-0">
                                                            <span title={`Avg. Fantasy Points: ${player.fppg?.toFixed(1) ?? '-'}`}>
                                                                <span className="text-accent-light-blue/90 font-medium">FPPG</span>
                                                                <span className="text-gray-100 ml-0.5">{player.fppg?.toFixed(1) ?? '-'}</span>
                                                            </span>
                                                            <span className={`${getOprkColorClass(player.oprk)}`} title={OPRK_TOOLTIP}>
                                                                <span className="text-accent-light-blue/90 font-medium">OPRK</span>
                                                                <span className="ml-0.5">{player.oprk?.toFixed(0) ?? '-'}</span>
                                                            </span>
                                                            <InjuryBadge status={player.injury_status} details={player.injury_details} />
                                                        </div>
                                                    </div>

                                                    {/* RIGHT COLUMN: Availability, matchup and time */}
                                                    <div className="flex flex-col items-center flex-shrink-0">
                                                        <div className="flex min-h-[18px] items-center justify-center">
                                                            <AvailabilityBadge status={player.availability_status} isLocked={isLocked} />
                                                        </div>
                                                        {isContest && hasEspnMatchData ? (
                                                            <span
                                                                className="text-badge sm:text-xs cursor-pointer hover:text-accent-light-blue active:text-accent-light-blue transition-colors font-medium text-accent-light-blue/80 underline decoration-dotted underline-offset-2"
                                                                onClick={(e) => { e.stopPropagation(); onMatchInfoClick?.(player); }}
                                                                role="button"
                                                                tabIndex={0}
                                                                title="View match details"
                                                            >
                                                                {matchupDisplay}
                                                            </span>
                                                        ) : (
                                                            <span className="text-badge sm:text-xs text-gray-100" title={matchupDisplay}>
                                                                {matchupDisplay}
                                                            </span>
                                                        )}

                                                        {hasEspnMatchData && !isPreMatch ? (
                                                            <span className={`text-badge sm:text-xs font-semibold mt-0.5 lg:mt-0 ${isFinalMatch ? 'text-gray-500' : 'text-green-400'}`}>
                                                                {player.match_clock}
                                                            </span>
                                                        ) : player.match_time ? (
                                                            <span className={`text-badge sm:text-xs mt-0.5 lg:mt-0 ${formatMatchDateTime(player.match_time, now) ? 'text-white' : 'text-gray-100'}`}>
                                                                {formatMatchDateTime(player.match_time, now) ?? formatMatchTime(player.match_time, false)}
                                                            </span>
                                                        ) : null}
                                                    </div>

                                                </div>
                                            </div>

                                            {/* Right side: score (match started) or salary+remove (match not started) */}
                                            {isLocked ? (
                                                <div
                                                    className="flex-shrink-0 ml-1 cursor-pointer underline decoration-dotted underline-offset-2"
                                                    onClick={(e) => { e.stopPropagation(); onScoreClick?.(player); }}
                                                    title="View Score Breakdown"
                                                >
                                                    <AnimatedScore score={score} previousScore={previousScore} />
                                                </div>
                                            ) : (
                                                <div className="flex-shrink-0 flex flex-row items-center gap-2 lg:gap-1.5 ml-1">
                                                     <span className="text-accent-yellow font-semibold text-xs sm:text-sm lg:text-xs">
                                                         ${(player.dfs_salary ?? 0).toLocaleString()}
                                                     </span>
                                                     {isContest ? (
                                                         <button
                                                            onClick={(e) => { e.stopPropagation(); onNavigateToBuilder?.(); }}
                                                            className="p-2.5 -m-1.5 sm:p-2 sm:-m-1 lg:p-0 lg:m-0 focus:outline-none"
                                                            title="Edit Lineup"
                                                            aria-label={`Edit lineup`}
                                                         >
                                                             <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 lg:w-4 lg:h-4 rounded-full font-bold text-badge lg:text-nano transition-colors duration-150 ease-in-out bg-secondary hover:bg-red-700 active:bg-red-700 text-white">✕</span>
                                                         </button>
                                                     ) : (
                                                         <button
                                                            onClick={(e) => { e.stopPropagation(); onRemovePlayer(player.id); }}
                                                            className="p-2.5 -m-1.5 sm:p-2 sm:-m-1 lg:p-0 lg:m-0 focus:outline-none"
                                                            title="Remove Player"
                                                            aria-label={`Remove ${formatPlayerDisplayName(player)}`}
                                                         >
                                                             <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 lg:w-4 lg:h-4 rounded-full font-bold text-badge lg:text-nano transition-colors duration-150 ease-in-out bg-secondary hover:bg-red-700 active:bg-red-700 text-white">✕</span>
                                                         </button>
                                                     )}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex-grow flex items-center space-x-2 sm:space-x-3">
                                                <span className="font-bold text-xs w-7 text-center flex-shrink-0 text-accent-yellow/40">
                                                    {slotKey}
                                                </span>
                                                <span className="text-sm text-gray-300">
                                                    Add {slotLabel}
                                                </span>
                                            </div>
                                            <div className="flex-shrink-0 ml-1">
                                                <span className="font-bold text-lg lg:text-sm w-6 h-6 sm:w-7 sm:h-7 lg:w-5 lg:h-5 rounded-full flex items-center justify-center bg-green-600/20 text-green-400">
                                                    +
                                                </span>
                                            </div>
                                        </>
                                     )}
                                </div>
                            );

                            // In contest mode, wrap locked player rows with score highlight
                            if (isContest && isLocked && player) {
                                return (
                                    <ScoreHighlightWrapper key={rowKey} score={score} className="rounded">
                                        {rowContent}
                                    </ScoreHighlightWrapper>
                                );
                            }

                            return rowContent;
                         })}
                     </div>
                </div>
            );
        })}
      </div>
    </div>
  );
}

export default CurrentLineup;
